import React, { useState } from 'react';
import { Modal, Form, Input, Button, notification, Alert } from 'antd';
import { KeyOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import axios from 'axios';
import { id, tokenName } from '../utils/data';
import { UpdatePasswordUrl, extractErrorMessage } from '../utils/network';
import { getAuthToken } from '../utils/functions';

interface ChangePasswordModalProps {
  open: boolean;
  onCancel: () => void;
  userId?: string | number;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onCancel,
  userId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = () => {
    form.resetFields();
    setErrorMessage(null);
    onCancel();
  };

  const handleSubmit = async (values: { password: string; confirm_password?: string }) => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const currentUserId = userId || localStorage.getItem(id);

      if (!currentUserId) {
        const errorText = 'Unable to find user identifier. Please re-login and try again.';
        setErrorMessage(errorText);
        notification.error({
          message: 'Error',
          description: errorText,
          placement: 'topRight',
        });
        setLoading(false);
        return;
      }

      const headers = getAuthToken();
      const payload = {
        user_id: String(currentUserId),
        password: values.password,
      };

      await axios.post(
        UpdatePasswordUrl,
        payload,
        headers ? headers : undefined
      );

      notification.success({
        message: 'Password Updated',
        description: 'Your password has been changed successfully.',
        placement: 'topRight',
      });

      handleClose();
    } catch (error: any) {
      console.error('Change password failed:', error);
      const msg = extractErrorMessage(error) || 'Failed to update password. Please try again.';
      setErrorMessage(msg);
      notification.error({
        message: 'Error',
        description: msg,
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      width={420}
      className="change-password-modal"
    >
      <div className="p-2 md:p-3">
        {/* Header Icon & Title */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#4682B4]/10 text-[#4682B4] flex items-center justify-center mx-auto mb-3">
            <KeyOutlined className="text-[24px]" />
          </div>
          <h3 className="font-['Lexend',sans-serif] text-[20px] font-semibold text-[#1F2A37] mb-1">
            Change Password
          </h3>
          <p className="text-[13px] text-[#64748b] font-normal m-0">
            Enter your new password below to update your account credentials.
          </p>
        </div>

        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            className="mb-4 rounded-lg text-[13px]"
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          autoComplete="off"
        >
          {/* New Password */}
          <Form.Item
            name="password"
            label={
              <span className="font-['Lexend',sans-serif] text-[13px] font-medium text-[#374151]">
                New Password
              </span>
            }
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password
              placeholder="Enter new password"
              prefix={<LockOutlined className="text-[#9ca3af] mr-1.5 text-[15px]" />}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#4682B4" />
                ) : (
                  <EyeInvisibleOutlined className="text-[#9ca3af]" />
                )
              }
              className="h-[44px] rounded-xl border-[#d1d5db] hover:border-[#4682B4] focus:border-[#4682B4] font-['Lexend',sans-serif] text-[14px]"
            />
          </Form.Item>

          {/* Confirm Password */}
          <Form.Item
            name="confirm_password"
            label={
              <span className="font-['Lexend',sans-serif] text-[13px] font-medium text-[#374151]">
                Confirm Password
              </span>
            }
            dependencies={['password']}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm new password"
              prefix={<LockOutlined className="text-[#9ca3af] mr-1.5 text-[15px]" />}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#4682B4" />
                ) : (
                  <EyeInvisibleOutlined className="text-[#9ca3af]" />
                )
              }
              className="h-[44px] rounded-xl border-[#d1d5db] hover:border-[#4682B4] focus:border-[#4682B4] font-['Lexend',sans-serif] text-[14px]"
            />
          </Form.Item>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button
              type="button"
              className="h-11 bg-[#f1f5f9] text-[#475569] rounded-xl font-['Lexend',sans-serif] font-medium text-[14px] hover:bg-[#e2e8f0] transition-all cursor-pointer border-none"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-11 bg-[#4682B4] text-white rounded-xl font-['Lexend',sans-serif] font-medium text-[14px] hover:bg-[#3a6d96] shadow-[0_4px_12px_rgba(70,130,180,0.2)] transition-all cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
